<template>
	<v-alert
		v-if="visible"
		color="primary"
		variant="tonal"
		icon="fal fa-rotate"
		prominent
		closable
		class="ma-3"
		role="status"
		data-test="legacy-sync-update-banner"
		:title="translate('component.legacySyncBanner.title')"
		:close-label="translate('component.legacySyncBanner.dismiss')"
		@click:close="dismiss"
	>
		<p class="text-body-2 mb-3 legacy-sync-banner__message">
			{{ translate('component.legacySyncBanner.message') }}
		</p>
		<ButtonIcon
			color="primary"
			variant="flat"
			icon="fas fa-download"
			text="component.legacySyncBanner.download"
			:loading="isFetchingLink || isLoading"
			data-test="legacy-sync-update-download"
			@click="download"
		/>
		<p
			v-if="downloadFailed"
			class="text-body-2 text-medium-emphasis mt-2 mb-0 legacy-sync-banner__error"
			data-test="legacy-sync-update-error"
		>
			{{ translate('component.legacySyncBanner.downloadError') }}
		</p>
	</v-alert>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/system/user'
import { useClientStore } from '@/stores/system/client'
import { useTranslate } from 'composables/useTranslate.ts'
import useFileHandler from '@/composables/useFileHandler'
import { syncService } from '@/services/api'

const { translate } = useTranslate()
const { isLoggedIn, isAdmin } = storeToRefs(useUserStore())
const { syncUpdatePending } = storeToRefs(useClientStore())
const { downloadFile, isLoading } = useFileHandler()

const dismissed = ref(false)
const isFetchingLink = ref(false)
const downloadFailed = ref(false)

// Admin-only, only for tenants the backend flags, dismissible for the session.
const visible = computed(
	() =>
		isLoggedIn.value &&
		isAdmin.value &&
		syncUpdatePending.value === true &&
		!dismissed.value
)

async function download(): Promise<void> {
	if (isFetchingLink.value || isLoading.value) return

	downloadFailed.value = false
	isFetchingLink.value = true

	try {
		// Fetched on click, not on load: a page view should not cost a request.
		// The address is stable and public; the API call is the entitlement
		// check, and the datendienst serves the current release behind it.
		const { data } = await syncService.installerDownload()
		const downloadUrl = new URL(data.url)
		if (!['http:', 'https:'].includes(downloadUrl.protocol)) {
			throw new Error('Invalid installer download URL protocol')
		}
		await downloadFile(downloadUrl.toString(), 'HPMobileSyncConfigurator.zip')
	} catch {
		// 403 (no longer admin / tenant no longer outdated) and 503 (datendienst
		// unreachable or has no package) both land here as a calm inline
		// message — never a silent no-op, never the raw backend error text.
		downloadFailed.value = true
	} finally {
		isFetchingLink.value = false
	}
}

function dismiss(): void {
	dismissed.value = true
}
</script>

<style scoped>
/* Keep the message at a comfortable reading measure even on wide content. */
.legacy-sync-banner__message,
.legacy-sync-banner__error {
	max-width: 70ch;
}
</style>
